import { useState, useEffect } from "react";

// Custom translation hook that loads from JSON files
export const useTranslation = (namespace: string = "dashboard") => {
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [translations, setTranslations] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage") || "EN";
    setCurrentLanguage(savedLanguage.toLowerCase());

    // Load translation files
    loadTranslations(savedLanguage.toLowerCase(), namespace);

    // Listen for language changes
    const handleLanguageChange = () => {
      const newLanguage = localStorage.getItem("selectedLanguage") || "EN";
      const langCode = newLanguage.toLowerCase();
      setCurrentLanguage(langCode);
      loadTranslations(langCode, namespace);
    };

    window.addEventListener("languageChanged", handleLanguageChange);

    return () => {
      window.removeEventListener("languageChanged", handleLanguageChange);
    };
  }, [namespace]);

  const loadTranslations = async (langCode: string, ns: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/locales/${langCode}/${ns}.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setTranslations(data);
    } catch (error) {
      console.error(`Error loading translations for ${langCode}/${ns}:`, error);
      // Fallback to English if translation fails
      if (langCode !== "en") {
        try {
          const fallbackResponse = await fetch(`/locales/en/${ns}.json`);
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            setTranslations(fallbackData);
          }
        } catch (fallbackError) {
          console.error("Error loading fallback translations:", fallbackError);
          setTranslations({});
        }
      } else {
        setTranslations({});
      }
    } finally {
      setLoading(false);
    }
  };

  // Translation function that navigates nested objects
  const t = (key: string, options?: any) => {
    const keys = key.split(".");
    let value = translations;

    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        if (!loading) {
          console.warn(`Translation key "${key}" not found in ${currentLanguage}/${namespace}`);
        }
        return key; // Return key if translation not found
      }
    }

    // Handle interpolation if options provided
    if (options && typeof value === "string") {
      return value.replace(/\{\{(\w+)\}\}/g, (match, key) => (options[key] !== undefined ? options[key] : match));
    }

    return value || key;
  };

  return {
    t,
    currentLanguage,
    loading,
    changeLanguage: (langCode: string) => {
      localStorage.setItem("selectedLanguage", langCode.toUpperCase());
      window.dispatchEvent(new Event("languageChanged"));
    },
  };
};
